import { ToastType } from '@/features/shared/constants/toastTypes'
import { useIsPage } from '@/features/shared/hooks/useIsPage'
import { useShowToast } from '@/features/shared/state/Toast/hooks'
import { useUserStoreName } from '@/features/account/services/User/hooks'
import {
  type AvailablePromotionsPayload,
  type Promotion,
  PromotionTypes,
} from '@/features/shop/services/Campaigns/types'
import {
  getCpgPromotionByCategoryId,
  getOrderDiscountPromotionValues,
  shouldDisplay,
} from '@/features/shop/services/Campaigns/utils'
import { useMemo } from 'react'
import { useQueryCart } from '@/features/shop/services/Cart/queries'
import { useQueryAvailablePromos } from '@/features/shop/services/Campaigns/queries'

export const useFetchPromosAndShowToast = () => {
  const showMarketingOrderPromoToast = useMarketingOrderPromoToast()

  return (prevValue: number, currentPromotions: AvailablePromotionsPayload): boolean => {
    const { availablePromotions } = currentPromotions
    const nextOrderDiscountPromotion =
      availablePromotions.find((p) => p.type === PromotionTypes.OrderDiscount) ?? {}
    if (nextOrderDiscountPromotion.promotion_id) {
      const { progress: thresholdProgress } = nextOrderDiscountPromotion.thresholds?.[0] ?? {}
      const progress = nextOrderDiscountPromotion.progress ?? thresholdProgress
      // only show toast if the progress has just gone over the threshold (75%) and not at 100% threshold
      if (progress && prevValue && progress >= 0.75 && progress !== 1) {
        showMarketingOrderPromoToast(prevValue, nextOrderDiscountPromotion)
        return true
      }
    }
    return false
  }
}

const useMarketingOrderPromoToast = () => {
  const { showToast } = useShowToast()
  return (prevValue: number, promotion: Promotion) => {
    const { display_name = '', promotion_params, progress: promotionProgress } = promotion
    const { progress: progressThreshold } = promotion.thresholds?.[0] ?? {}
    const progress = promotionProgress ?? progressThreshold
    // get threshold data for promotion
    const { effect: creditValue = 0, remaining_amount = 0 } = promotion.thresholds?.[0] || {}
    const formattedCreditValue = creditValue / 100
    const formattedRemainingAmount = Math.ceil(remaining_amount / 100)

    showToast(ToastType.MARKETING_ORDER_PROMO_TOAST, {
      optionalStyling: { width: '100%' },
      ariaLabel: `${promotion.progress_header}`,
      props: {
        prevProgress: prevValue,
        progress: progress ?? 0,
        remainingAmount: formattedRemainingAmount,
        creditValue: formattedCreditValue,
        promotionId: promotion.promotion_id ?? '',
        categoryId: promotion_params?.category_id ?? 0,
        displayName: display_name ?? '',
        progressHeader: promotion.progress_header ?? '',
      },
    })
  }
}

export const useFeaturePromoToast = () => {
  const storeName = useUserStoreName()
  const { showToast } = useShowToast()
  const { RETAILER_FEATURED_PROMOTIONS: isFeaturePromoPage } = useIsPage()

  return (
    currentPromotions: AvailablePromotionsPayload,
    prevPromotions?: AvailablePromotionsPayload,
    categoryId?: number,
  ): boolean => {
    const currentCpgPromotion = getCpgPromotionByCategoryId(
      currentPromotions.availablePromotions,
      categoryId ?? 0,
    )
    if (currentCpgPromotion && categoryId) {
      const prevCpgPromotion = getCpgPromotionByCategoryId(
        prevPromotions?.availablePromotions ?? [],
        categoryId,
      )
      const prevThreshold = prevCpgPromotion?.thresholds ?? []
      const prevProgress = prevCpgPromotion?.progress
        ? prevCpgPromotion.progress
        : (prevThreshold.at(-1)?.progress ?? 0)

      const {
        thresholds = [],
        promotion_params,
        promotion_id: promotionId,
        display_name: categoryName,
        progress_header: progressHeader,
        progress_subheader: progressSubheader,
      } = currentCpgPromotion
      const threshold = thresholds?.[0]
      const { progress: thresholdProgress = 0 } = threshold || {}
      const progress = currentCpgPromotion.progress ?? thresholdProgress
      if (prevProgress === progress) return false
      const imageUrl = promotion_params?.images?.[0]?.image_url || ''

      showToast(ToastType.FEATURE_PROMOTIONS_TOAST, {
        ariaLabel: `Item quantity updated. ${progressHeader} at ${storeName}.`,
        optionalStyling: {
          width: '100%',
          // when on the FeaturedPromotion page, we want the progress toast to appear in the bottom right
          // as there is already a progress banner on this page
          ...(isFeaturePromoPage && {
            top: 'unset',
            bottom: '16px',
          }),
        },
        props: {
          promotionId: promotionId ?? '',
          categoryId,
          categoryName: categoryName ?? '',
          imageUrl,
          progress: progress * 100,
          prevProgress,
          progressHeader: progressHeader ?? '',
          progressSubheader: progressSubheader ?? '',
        },
      })
      return true
    }
    return false
  }
}

export const orderByPromotionTypeAndEffectImportance = (
  promotion1: Promotion,
  promotion2: Promotion,
) => {
  const orderedTypes = [PromotionTypes.OrderDiscount, undefined, null]
  return promotion1.type === promotion2.type
    ? (promotion2.thresholds?.[0]?.effect ?? 0) - (promotion1.thresholds?.[0]?.effect ?? 0)
    : orderedTypes.indexOf(promotion1.type) - orderedTypes.indexOf(promotion2.type)
}

export const useGetHeroBannerPromotion = () => {
  const { availablePromotions = [] } = useQueryAvailablePromos().data ?? {}

  const sortedPromotionsByType = availablePromotions
    .filter((promotion) => shouldDisplay(promotion))
    .sort(orderByPromotionTypeAndEffectImportance)
  return sortedPromotionsByType.shift()
}

export const useCampaignPromotions = () => {
  const cartResponse = useQueryCart().data
  const { availablePromotions, dollarTotalQualifyingCredit } = cartResponse?.promotions ?? {}
  return {
    promotions: availablePromotions ?? [],
    dollarQualifyingCredit: dollarTotalQualifyingCredit ?? 0,
  }
}

export const useOrderDiscountPromotion = () => {
  const cartResponse = useQueryCart().data
  const { availablePromotions } = cartResponse?.promotions ?? {}

  return useMemo(() => getOrderDiscountPromotionValues(availablePromotions), [availablePromotions])
}
